// Single source of truth for design tokens.
// Used to generate both Chakra theme and Tailwind config so the two systems stay aligned.

// Quantiv Media brand: red #E31E24 on black #000000, Familjen Grotesk.
// Scraped from quantivmedia.com and re-verified against the logo's own pixels.
//
// WHY accent.primary AND accent.primaryText ARE DIFFERENT REDS:
// brand red on black is 4.48:1 — it passes WCAG AA for large text and UI
// components (3:1) but FAILS for body text (4.5:1). So the brand red is a FILL
// colour only (buttons, active nav, bars, borders) where it carries white text
// at 4.69:1, and a lighter tint carries any red that is itself text or a link:
//   dark  #FF4D4D on #000000 -> 6.42:1
//   light #B81419 on #FFFFFF -> 6.66:1
// Use accent.primary for backgrounds, accent.primaryText for `color:`.
export const colors = {
  light: {
    // Surfaces (background layers)
    bg:        { canvas: "#FAFAFA", surface: "#FFFFFF", subtle: "#F4F4F4", elevated: "#FFFFFF" },
    border:    { subtle: "#EAEAEA", default: "#DCDCDC", strong: "#B4B4B4" },
    text:      { primary: "#0A0A0B", secondary: "#52525B", tertiary: "#6E6E75", inverted: "#FFFFFF" },
    accent:    { primary: "#E31E24", primaryHover: "#C41A1F", primaryText: "#B81419", primarySoft: "#FFF0F0", primaryStrong: "#8F0F13" },
    semantic:  { success: "#10B981", warning: "#F59E0B", danger: "#EF4444", info: "#0EA5E9" },
    semanticSoft: { success: "#ECFDF5", warning: "#FFFBEB", danger: "#FEF2F2", info: "#F0F9FF" },
  },
  dark: {
    bg:        { canvas: "#000000", surface: "#0A0A0B", subtle: "#141416", elevated: "#1A1A1D" },
    border:    { subtle: "#1F1F22", default: "#2E2E33", strong: "#4D4D4D" },
    text:      { primary: "#F4F4F4", secondary: "#A1A1A6", tertiary: "#78787F", inverted: "#000000" },
    accent:    { primary: "#E31E24", primaryHover: "#FF3B41", primaryText: "#FF4D4D", primarySoft: "#2A0A0C", primaryStrong: "#FF6B70" },
    semantic:  { success: "#34D17D", warning: "#FBBF24", danger: "#F87171", info: "#38BDF8" },
    semanticSoft: { success: "#0B2A1F", warning: "#2A2010", danger: "#2A1414", info: "#0C2030" },
  },
  // Functional dept role colors (work across both themes; kept from existing system)
  fn: {
    video: "#A855F7", design: "#EC4899", content: "#F59E0B",
    social_media: "#0EA5E9", seo: "#10B981", web_dev: "#6366F1",
  },
};

export const radius = {
  none: "0",
  sm: "6px",
  md: "10px",
  lg: "14px",
  xl: "20px",
  "2xl": "28px",
  pill: "9999px",
};

export const shadows = {
  light: {
    xs: "0 1px 2px rgba(15, 23, 42, 0.04)",
    sm: "0 2px 4px rgba(15, 23, 42, 0.05), 0 1px 2px rgba(15, 23, 42, 0.04)",
    md: "0 8px 16px rgba(15, 23, 42, 0.06), 0 2px 4px rgba(15, 23, 42, 0.04)",
    lg: "0 16px 32px rgba(15, 23, 42, 0.08), 0 4px 8px rgba(15, 23, 42, 0.04)",
    xl: "0 24px 48px rgba(15, 23, 42, 0.12), 0 8px 16px rgba(15, 23, 42, 0.06)",
    glow: "0 0 0 1px rgba(227, 30, 36, 0.15), 0 8px 24px rgba(227, 30, 36, 0.12)",
    ring: "0 0 0 3px rgba(227, 30, 36, 0.18)",
  },
  dark: {
    xs: "0 1px 2px rgba(0, 0, 0, 0.5)",
    sm: "0 2px 4px rgba(0, 0, 0, 0.4), 0 1px 2px rgba(0, 0, 0, 0.5)",
    md: "0 8px 16px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.03)",
    lg: "0 16px 32px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.04)",
    xl: "0 24px 48px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
    glow: "0 0 0 1px rgba(227, 30, 36, 0.35), 0 8px 24px rgba(227, 30, 36, 0.22)",
    ring: "0 0 0 3px rgba(227, 30, 36, 0.36)",
  },
};

export const motion = {
  duration: { fast: "120ms", base: "200ms", slow: "320ms" },
  ease: {
    standard: "cubic-bezier(0.4, 0, 0.2, 1)",
    in: "cubic-bezier(0.4, 0, 1, 1)",
    out: "cubic-bezier(0, 0, 0.2, 1)",
    spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  },
};

export const typography = {
  fontFamily: '"Familjen Grotesk", "Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
  fontMono: '"JetBrains Mono", ui-monospace, monospace',
  sizes: {
    xs: "11px", sm: "13px", base: "14px", md: "15px",
    lg: "17px", xl: "20px", "2xl": "24px", "3xl": "32px", "4xl": "44px",
  },
  weights: { regular: 400, medium: 500, semibold: 600, bold: 700 },
  lineHeights: { tight: 1.2, snug: 1.3, base: 1.5, relaxed: 1.6 },
};

export const spacing = {
  0: "0", 0.5: "2px", 1: "4px", 1.5: "6px", 2: "8px", 2.5: "10px",
  3: "12px", 4: "16px", 5: "20px", 6: "24px", 8: "32px", 10: "40px",
  12: "48px", 16: "64px", 20: "80px",
};

const tokens = { colors, radius, shadows, motion, typography, spacing };
export default tokens;
